import { useEffect, useState } from "react";
import { Plus, Trash2, Loader2, Pencil, UtensilsCrossed } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ImageUpload from "@/components/ImageUpload";

const CATEGORIES = ["Breakfast", "All Day", "Mains", "Sides", "Desserts", "Drinks", "Alcohol"];
const empty = { category: "All Day", name: "", description: "", price: "", available: true, image_url: null };

export default function MenuPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/menu").then((r) => { setItems(r.data); setLoading(false); });
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...it, price: it.price ?? "" }); setOpen(true); };

  const save = async () => {
    if (!form.name) return toast.error("Name is required");
    const payload = { ...form, price: parseFloat(form.price) || 0 };
    try {
      if (editing) await api.put(`/menu/${editing.id}`, payload);
      else await api.post("/menu", payload);
      toast.success("Saved");
      setOpen(false);
      load();
    } catch { toast.error("Could not save"); }
  };

  const del = async (id) => { await api.delete(`/menu/${id}`); load(); };

  return (
    <div className="space-y-6" data-testid="menu-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl text-foreground mb-1">Room Service Menu</h1>
          <p className="text-muted-foreground text-sm">Items guests can order to their room — billed to their folio.</p>
        </div>
        <Button onClick={openNew} data-testid="add-menu-btn" className="bg-primary text-primary-foreground"><Plus className="w-4 h-4 mr-2" /> Add item</Button>
      </div>

      {loading ? <Loader2 className="w-5 h-5 animate-spin text-accent" /> : items.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-border rounded-md">
          <UtensilsCrossed className="w-8 h-8 text-muted-foreground mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-muted-foreground text-sm">No menu items yet.</p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-md divide-y divide-border">
          {items.map((it) => (
            <div key={it.id} className="p-4 flex items-center justify-between" data-testid={`menu-item-${it.id}`}>
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-foreground font-medium">{it.name}</p>
                  <Badge variant="secondary" className="text-xs">{it.category}</Badge>
                  {!it.available && <Badge className="bg-red-100 text-red-700 border-0 text-xs">Hidden</Badge>}
                </div>
                <p className="text-sm text-muted-foreground mt-0.5">{it.description}</p>
              </div>
              <div className="flex items-center gap-4">
                <span className="font-serif text-foreground">£{it.price}</span>
                <Button size="sm" variant="outline" onClick={() => openEdit(it)} data-testid={`edit-menu-${it.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                <Button size="sm" variant="outline" onClick={() => del(it.id)} data-testid={`delete-menu-${it.id}`} className="text-red-600"><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-serif text-xl">{editing ? "Edit item" : "Add menu item"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Name</Label><Input data-testid="menu-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="menu-category" className="mt-1.5 w-full h-10 rounded-md border border-input px-3 text-sm bg-background">
                  {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div><Label>Price (£)</Label><Input data-testid="menu-price" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div><Label>Description</Label><Textarea data-testid="menu-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="mt-1.5" /></div>
            <div><Label className="mb-1.5 block">Image</Label><ImageUpload value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} testId="menu-image" /></div>
            <div className="flex items-center justify-between">
              <Label>Visible to guests</Label>
              <Switch checked={form.available} onCheckedChange={(v) => setForm({ ...form, available: v })} data-testid="menu-available" />
            </div>
            <Button onClick={save} data-testid="save-menu-btn" className="w-full bg-primary text-primary-foreground">Save item</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
